import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { workSamples, capabilities, caseStudies, clients, background, RESUME_URL } from "@/data";

export default function Home() {
  const [activeVideo, setActiveVideo] = useState(null);
  const [currentWorkIndex, setCurrentWorkIndex] = useState(0);
  const [expandedCapability, setExpandedCapability] = useState(null);
  const location = useLocation();

  const workScrollRef = useRef(null);

  // Deep-link: /portfolio and /#recent-work both land on the Portfolio Work section.
  // Runs on mount and whenever the pathname/hash changes.
  useEffect(() => {
    const wantsPortfolio =
      location.pathname === "/portfolio" || location.hash === "#recent-work";
    if (!wantsPortfolio) return;
    // Defer so layout + fonts are ready before scrolling.
    const t = setTimeout(() => {
      const el = document.getElementById("recent-work");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [location.pathname, location.hash]);

  // Scroll reveal observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: "0px 0px -100px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
        }
      });
    }, observerOptions);

    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const scrollToWork = (index) => {
    const container = workScrollRef.current;
    if (container) {
      const scrollAmount = index * (container.offsetWidth * 0.85);
      container.scrollTo({ left: scrollAmount, behavior: "smooth" });
      setCurrentWorkIndex(index);
    }
  };

  const toggleCapability = (id) => {
    setExpandedCapability(expandedCapability === id ? null : id);
  };

  return (
    <div className="App">
      {/* Navigation (always visible) */}
      <div className="nav-indicator" data-testid="nav-indicator">
        <a href="#hero" className="nav-name">
          Dan McNamara
        </a>
        <div className="nav-links">
          <a href="#recent-work">Portfolio Work</a>
          <a href="#reel">Reel</a>
          <a href="#work">Selected Work</a>
          <a href="#contact">Contact</a>
          <a
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-resume"
            data-testid="nav-resume"
          >
            Resume
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <section id="hero" className="hero-section" data-testid="hero-section">
        <div className="hero-content">
          <h1 className="hero-headline reveal" data-testid="hero-headline">
            Dan
            <br />
            McNamara
          </h1>
          <div className="hero-meta reveal">
            <p className="hero-role-bold">
              VFX · Motion Graphics · Creative Direction · AI Video
            </p>
            <div className="hero-divider"></div>
            <p className="hero-intro">
              Concept, design, motion, VFX, and AI-assisted workflows for fast-turn,
              high-end content.
            </p>
          </div>
        </div>
      </section>

      {/* Recent Portfolio Work Section */}
      <section
        id="recent-work"
        className="case-studies-section reveal"
        data-testid="recent-work-section"
      >
        <div className="section-header">
          <span className="section-number">01</span>
          <h2 className="section-title">Recent Portfolio Work</h2>
        </div>

        <div className="case-studies-list">
          {caseStudies.map((study) => {
            const meta = study.category
              ? study.category.replace(/^Case Study\s*[·\-—]\s*/i, "")
              : "";
            const aspectClass = study.imageAspect
              ? `cs-feat-image-${study.imageAspect}`
              : "cs-feat-image-square";
            return (
              <Link
                key={study.id}
                to={study.path}
                className="case-study-feature"
                data-testid={`case-study-${study.slug}`}
              >
                <div className="case-study-feature-inner">
                  <span className="case-study-kicker">Portfolio</span>
                  <h3 className="case-study-feature-title">{study.title}</h3>
                  <p className="case-study-feature-sub">{study.subtitle}</p>
                  {meta && (
                    <p className="case-study-feature-meta">{meta}</p>
                  )}
                  <div className="case-study-cta">
                    <span>View Project</span>
                    <span className="case-study-arrow">→</span>
                  </div>
                </div>
                {study.image && (
                  <div className={`case-study-feature-image ${aspectClass}`}>
                    <img
                      src={study.image}
                      alt={`${study.title} — preview`}
                      loading="lazy"
                    />
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </section>

      {/* Showreel Section */}
      <section
        id="reel"
        className="reel-section reveal"
        data-testid="reel-section"
      >
        <div className="section-header">
          <span className="section-number">02</span>
          <h2 className="section-title">Showreel</h2>
        </div>
        <div className="reel-container">
          <div className="video-wrapper">
            <iframe
              data-testid="youtube-reel"
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/ahW8OHiyjTk"
              title="Dan McNamara - Showreel 2022"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section
        id="capabilities"
        className="capabilities-section reveal"
        data-testid="capabilities-section"
      >
        <div className="section-header">
          <span className="section-number">03</span>
          <h2 className="section-title">Capabilities</h2>
        </div>
        <div className="capabilities-list">
          {capabilities.map((capability, index) => (
            <div
              key={capability.id}
              className={`capability-item ${
                expandedCapability === capability.id ? "expanded" : ""
              }`}
              data-testid={`capability-${capability.id}`}
            >
              <button
                className="capability-header"
                onClick={() => toggleCapability(capability.id)}
                data-testid={`capability-toggle-${capability.id}`}
              >
                <span className="capability-index">0{index + 1}</span>
                <h3 className="capability-title">{capability.title}</h3>
                <span className="capability-icon">
                  {expandedCapability === capability.id ? "−" : "+"}
                </span>
              </button>
              <div className="capability-content">
                <p>{capability.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Selected Work Section */}
      <section
        id="work"
        className="work-section reveal"
        data-testid="work-section"
      >
        <div className="section-header">
          <span className="section-number">04</span>
          <h2 className="section-title">Selected Work</h2>
          <div className="work-counter">
            <span className="current-index">
              {String(currentWorkIndex + 1).padStart(2, "0")}
            </span>
            <span className="counter-separator">/</span>
            <span className="total-index">
              {String(workSamples.length).padStart(2, "0")}
            </span>
          </div>
        </div>

        <div
          className="work-scroll-container"
          ref={workScrollRef}
          data-testid="work-scroll-container"
        >
          <div className="work-scroll-track">
            {workSamples.map((work, index) => (
              <div
                key={work.id}
                className="work-item-horizontal"
                onClick={() => setActiveVideo(work)}
                data-testid={`work-item-${work.id}`}
              >
                <div className="work-video-container">
                  <video
                    src={work.videoUrl}
                    className="work-video"
                    muted
                    loop
                    playsInline
                  />
                  <div className="work-play-overlay">
                    <span className="play-icon">▶</span>
                  </div>
                </div>
                <div className="work-meta">
                  <span className="work-index">0{index + 1}</span>
                  <h3 className="work-title">{work.title}</h3>
                  <p className="work-category">{work.category}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="work-navigation">
          {workSamples.map((_, index) => (
            <button
              key={index}
              className={`work-nav-dot ${
                currentWorkIndex === index ? "active" : ""
              }`}
              onClick={() => scrollToWork(index)}
              aria-label={`Go to work ${index + 1}`}
              data-testid={`work-nav-${index}`}
            />
          ))}
        </div>

        <p className="work-note">
          Additional work and AI-generated projects available upon request
        </p>
      </section>

      {/* Selected Clients Strip */}
      <section
        id="clients"
        className="clients-strip reveal"
        data-testid="clients-strip"
        aria-label="Selected clients"
      >
        <span className="clients-kicker">Selected Clients</span>
        <ul className="clients-list">
          {clients.map((client, i) => (
            <li
              key={client.name}
              className="clients-item"
              data-testid={`client-${i}`}
            >
              {client.logo ? (
                <img
                  src={client.logo}
                  alt={client.name}
                  title={client.name}
                  className="client-logo"
                  loading="lazy"
                />
              ) : (
                <span
                  className={`client-wordmark client-wordmark-${
                    client.wordmarkFont || "sans"
                  }`}
                  title={client.name}
                >
                  {client.wordmark}
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* Background Section */}
      <section
        id="background"
        className="background-section reveal"
        data-testid="background-section"
      >
        <div className="section-header">
          <span className="section-number">05</span>
          <h2 className="section-title">Background</h2>
        </div>

        <div className="background-content">
          <p className="background-paragraph">{background.lede}</p>

          <h3 className="background-subhead">Experience</h3>
          <ol className="background-timeline">
            {background.experience.map((row) => (
              <li
                key={row.employer}
                className="background-row"
                data-testid={`bg-exp-${row.employer
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                <span className="background-row-span">{row.span}</span>
                <div className="background-row-body">
                  <p className="background-row-employer">{row.employer}</p>
                  <p className="background-row-role">{row.role}</p>
                  {row.note && (
                    <p className="background-row-note">{row.note}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>

          <h3 className="background-subhead">Independent Work &amp; Recognition</h3>
          <ul className="background-recognition">
            {background.recognition.map((item) => (
              <li key={item} className="background-recognition-item">
                {item}
              </li>
            ))}
          </ul>

          <p className="background-availability">
            Available for full-time and freelance work.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="contact-section reveal"
        data-testid="contact-section"
      >
        <div className="section-header">
          <span className="section-number">06</span>
          <h2 className="section-title">Contact</h2>
        </div>
        <div className="contact-content">
          <a
            href="mailto:dbmcnamara@gmail.com"
            className="contact-link contact-email"
            data-testid="contact-email"
          >
            dbmcnamara@gmail.com
          </a>
          <div className="contact-secondary">
            <a
              href="https://www.linkedin.com/in/danmcnamararocks/"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-link"
              data-testid="contact-linkedin"
            >
              LinkedIn
            </a>
            <span className="contact-divider">·</span>
            <a
              href={RESUME_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-link"
              data-testid="resume-link"
            >
              Resume
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <p>© 2026 Dan McNamara. Available for full-time and freelance work.</p>
      </footer>

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="modal-overlay"
          onClick={() => setActiveVideo(null)}
          data-testid="video-modal"
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setActiveVideo(null)}
              data-testid="modal-close-btn"
            >
              Close
            </button>
            <video
              src={activeVideo.videoUrl}
              controls
              autoPlay
              className="modal-video"
              data-testid="modal-video-player"
            />
          </div>
        </div>
      )}
    </div>
  );
}
